import { NError, NSuccess } from '@src/configs/const.config'
import { TResApi, TResApiErr } from '@src/configs/interface.config'
import { checkAuth } from '@src/libs/localStorage'
import {
  TAdminDealerMonthlyDiscount,
  TCloneMonthlyDiscountPolicy,
  TPolicyDocument,
  TPolicyValidationResult,
  TPolicyVersionSummary,
  TWritePolicyDocument,
} from '@src/modules'
import { notification } from 'antd'
import { useMutation, useQuery } from 'react-query'

import {
  cloneMonthlyDiscountConfig,
  getDealerMonthlyDiscount,
  getMonthlyDiscountConfig,
  getMonthlyDiscountVersions,
  publishMonthlyDiscountConfig,
  putMonthlyDiscountConfig,
  validateMonthlyDiscountConfig,
} from '../apis/monthly-discount-admin'
import { formatApiError } from './item-code-group-admin'

import { queryClient } from '..'

export const MONTHLY_DISCOUNT_CONFIG = 'MONTHLY_DISCOUNT_CONFIG'
export const MONTHLY_DISCOUNT_VERSIONS = 'MONTHLY_DISCOUNT_VERSIONS'
export const DEALER_MONTHLY_DISCOUNT = 'DEALER_MONTHLY_DISCOUNT'

const invalidateConfig = () => {
  queryClient.invalidateQueries([MONTHLY_DISCOUNT_CONFIG])
  queryClient.invalidateQueries([MONTHLY_DISCOUNT_VERSIONS])
}

export const useMonthlyDiscountConfig = (effectiveFrom?: number) => {
  const token = checkAuth()
  return useQuery<TResApi<TPolicyDocument | null>>(
    [MONTHLY_DISCOUNT_CONFIG, effectiveFrom ?? 'current'],
    () => getMonthlyDiscountConfig(effectiveFrom, token),
    { enabled: !!token },
  )
}

export const useMonthlyDiscountVersions = () => {
  const token = checkAuth()
  return useQuery<TResApi<TPolicyVersionSummary[]>>(
    [MONTHLY_DISCOUNT_VERSIONS],
    () => getMonthlyDiscountVersions(token),
    { enabled: !!token },
  )
}

/**
 * One dealer's month, read on their behalf (RPT-010).
 *
 * `userId`, `year` and `month` are all in the key: the cache must not answer a question about
 * May with March's figures the moment an operator changes the period picker.
 */
export const useDealerMonthlyDiscount = (
  userId: number | null,
  year: number,
  month: number,
  enabled = true,
) => {
  const token = checkAuth()
  return useQuery<TResApi<TAdminDealerMonthlyDiscount>>(
    [DEALER_MONTHLY_DISCOUNT, userId, year, month],
    () => getDealerMonthlyDiscount(userId as number, year, month, token),
    { enabled: !!token && !!userId && enabled, retry: false },
  )
}

export const useSaveMonthlyDiscountConfig = () => {
  const token = checkAuth()
  return useMutation((data: TWritePolicyDocument) => putMonthlyDiscountConfig(data, token), {
    onSuccess: (res: TResApi<TPolicyDocument>) => {
      invalidateConfig()
      notification.success({ message: NSuccess, description: res?.message })
    },
    // 409 (version is PUBLISHED/CLOSED) is shown inline by the page as a dedicated alert with a
    // clone shortcut (Dev Notes) — the raw toast here would just repeat that without the shortcut.
    onError: (error: TResApiErr & { status?: number }) => {
      if (error?.status !== 409) {
        notification.error({ message: NError, description: formatApiError(error?.message) })
      }
    },
  })
}

// Backs the on-blur auto-validate flow (AC7): BE has no "validate this unsaved payload" endpoint,
// only "validate what's persisted for this effectiveFrom" — so seeing an error as you type means
// silently persisting the draft first. Silent on both outcomes at the hook level (overriding the
// global toast) — this fires on every debounced blur, and both a success toast and an invalidate-
// triggered refetch here would surface/reset things right under the operator's hands. The caller
// still attaches its own per-call onSuccess/onError (react-query runs both), e.g. to catch a 409
// (version got locked mid-edit) and show the dedicated inline alert instead of nothing.
export const useAutoSaveMonthlyDiscountConfig = () => {
  const token = checkAuth()
  return useMutation((data: TWritePolicyDocument) => putMonthlyDiscountConfig(data, token), {
    onSuccess: () => undefined,
    // Typed (not just `() => undefined`) so TError is pinned here instead of inferred as
    // `unknown` — otherwise a per-call onError elsewhere (reading `error.status`) fails to typecheck.
    onError: (_error: TResApiErr & { status?: number }) => undefined,
  })
}

export const useCloneMonthlyDiscountConfig = () => {
  const token = checkAuth()
  return useMutation(
    (data: TCloneMonthlyDiscountPolicy) => cloneMonthlyDiscountConfig(data, token),
    {
      onSuccess: (res: TResApi<TPolicyDocument>) => {
        invalidateConfig()
        notification.success({ message: NSuccess, description: res?.message })
      },
    },
  )
}

export const useValidateMonthlyDiscountConfig = () => {
  const token = checkAuth()
  return useMutation<TResApi<TPolicyValidationResult>, TResApiErr, number>(
    (effectiveFrom) => validateMonthlyDiscountConfig(effectiveFrom, token),
    {
      // Silent on both outcomes — runs on every field blur (AC7) and again right before publish
      // (AC6); the caller renders canPublish/missingRates/overlappingCodes itself, so a toast per
      // call would just be noise on top of the inline grid highlighting.
      onSuccess: () => undefined,
      onError: () => undefined,
    },
  )
}

export const usePublishMonthlyDiscountConfig = () => {
  const token = checkAuth()
  return useMutation(
    (effectiveFrom: number) => publishMonthlyDiscountConfig(effectiveFrom, token),
    {
      onSuccess: (res: TResApi<TPolicyVersionSummary>) => {
        invalidateConfig()
        notification.success({ message: NSuccess, description: res?.message })
      },
    },
  )
}
